import { useMemo, useState } from "react";
import { toIdParam, usePayrollListParams } from "../payroll-lists.params";
import { CheckCircle, Download, FileCheck2, Search } from "lucide-react";
import ReactCountryFlag from "react-country-flag";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Button } from "@/components/ui/button";
import { ViewAction } from "@/components/table/actions";
import { PermissionButton } from "@/components/ui/permission-button";
import { Table, type ColumnDef, type SelectionDef } from "@/components/table";
import { PageDataStats } from "@/components/PageDataStats";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding, formatDateDmy } from "@/utils/helper";
import { ROUTES } from "@/constants/routes";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import {
  useListPayrollBranchesQuery,
  useListFinalSettlementsQuery,
  useGetFinalSettlementStatsQuery,
  useGenerateFinalSettlementWpsMutation,
  useMarkFinalSettlementPaidMutation,
  type IPayslipListItem,
} from "@/services/(company)/payrollRunsApi";
import { useGetAllUserDepartmentsQuery } from "@/services/(company)/departmentsApi";
import { PaymentMethodTypeEnum } from "@/utils/enums/payroll.enum";
import { useDownloadFinalSettlement } from "@/services/(company)/finalSettlementDownload";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const buildStatusOptions = (translate: TFunction) => [
  { value: "all", label: translate("company:payrolls.all_statuses") },
  { value: "PAID", label: translate("company:payrolls.paid") },
  { value: "PENDING", label: translate("common:status.pending") },
  { value: "HOLD", label: translate("company:payrolls.hold") },
  { value: "EXCLUDED", label: translate("company:payrolls.excluded") },
];

const FilterSelect = ({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-36 bg-white text-xs font-medium">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const formatAmount = (v: number | string) => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const safeDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : formatDateDmy(d);
};

const PAYMENT_METHOD_LABEL_KEYS: Record<string, string> = {
  [PaymentMethodTypeEnum.WPS]: "company:payrolls.wps_label",
  [PaymentMethodTypeEnum.BANK_TRANSFER]: "company:payrolls.bank_transfer",
  [PaymentMethodTypeEnum.CASH]: "company:payrolls.cash",
  [PaymentMethodTypeEnum.CHEQUE]: "company:payrolls.cheque",
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  PAID: "company:payrolls.paid",
  PENDING: "common:status.pending",
  HOLD: "company:payrolls.hold",
  EXCLUDED: "company:payrolls.excluded",
};

export const CompanyFinalSettlements = () => {
  const { t: translate } = useTranslation(["company", "common"]);
  usePageTitle(withBranding(translate("company:payrolls.final_settlement")));
  const navigate = useNavigate();

  // Search, filters and page all live in the URL — see useTableParams.
  const tp = usePayrollListParams();
  const { branch, department, status } = tp.filters;

  const { data: branchesResponse } = useListPayrollBranchesQuery();
  const branches = branchesResponse?.data ?? [];

  const { data: departmentsResponse } = useGetAllUserDepartmentsQuery({});
  const departments = departmentsResponse?.data ?? [];

  const branchOptions = [
    { value: "all", label: translate("common:ui_kit.all_branches") },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];

  const departmentOptions = [
    { value: "all", label: translate("company:payrolls.all_departments") },
    ...departments.map((d: { id: number | string; name: string }) => ({
      value: String(d.id),
      label: d.name,
    })),
  ];

  const { currentData, isFetching, refetch } = useListFinalSettlementsQuery({
    page: tp.page,
    limit: 20,
    ...(tp.committedSearch ? { search: tp.committedSearch } : {}),
    ...(toIdParam(branch) !== undefined
      ? { branchId: toIdParam(branch)! }
      : {}),
    ...(toIdParam(department) !== undefined
      ? { departmentId: toIdParam(department)! }
      : {}),
    ...(status !== "all" ? { status } : {}),
  });

  const rows = useMemo<IPayslipListItem[]>(
    () => currentData?.data ?? [],
    [currentData],
  );
  const totalPages =
    currentData?.meta?.totalPages ?? currentData?.meta?.lastPage ?? 1;

  const { data: statsRes, isLoading: statsLoading } =
    useGetFinalSettlementStatsQuery({
      ...(toIdParam(branch) !== undefined
        ? { branchId: toIdParam(branch)! }
        : {}),
      ...(toIdParam(department) !== undefined
        ? { departmentId: toIdParam(department)! }
        : {}),
    });
  const stats = statsRes?.data;
  // Each tile is the status filter it counts; the total is "no filter".
  const statCards = [
    {
      name: translate("company:payrolls.total_settlements"),
      value: stats?.total ?? 0,
      icon: "/icons/interface-icons/stats/payroll/icon-3d-cash-envelop.png",
      ...tp.statFilter({ status: "all" }),
    },
    {
      name: translate("company:payrolls.paid"),
      value: stats?.paid ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-green-check.png",
      ...tp.statFilter({ status: "PAID" }),
    },
    {
      name: translate("common:status.pending"),
      value: stats?.pending ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-clock-gold.png",
      ...tp.statFilter({ status: "PENDING" }),
    },
    {
      name: translate("company:payrolls.hold"),
      value: stats?.hold ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-pause.png",
      ...tp.statFilter({ status: "HOLD" }),
    },
    {
      name: translate("company:payrolls.excluded"),
      value: stats?.excluded ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-red-x.png",
      ...tp.statFilter({ status: "EXCLUDED" }),
    },
  ];

  const { download, isDownloading } = useDownloadFinalSettlement();
  const [generateWps, { isLoading: isGeneratingWps }] =
    useGenerateFinalSettlementWpsMutation();
  const [markPaid, { isLoading: isMarkingPaid }] =
    useMarkFinalSettlementPaidMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const selectedRunIds = useMemo(() => {
    const idSet = new Set(selectedIds.map(Number));
    const runIds = rows.filter((r) => idSet.has(r.id)).map((r) => r.runId);
    return [...new Set(runIds)];
  }, [selectedIds, rows]);

  const selectedUnpaidItemIds = useMemo(() => {
    const idSet = new Set(selectedIds.map(Number));
    return rows
      .filter((r) => idSet.has(r.id) && r.status !== "PAID")
      .map((r) => r.id);
  }, [selectedIds, rows]);

  const handleMarkPaid = async () => {
    if (!selectedUnpaidItemIds.length) return;
    let ok = 0;
    let fail = 0;
    for (const id of selectedUnpaidItemIds) {
      try {
        await markPaid({ id }).unwrap();
        ok++;
      } catch {
        fail++;
      }
    }
    if (ok)
      toast.success(
        translate("company:payrolls.marked_settlements_as_paid_count", {
          count: ok,
        }),
      );
    if (fail)
      toast.error(
        translate("company:payrolls.failed_to_mark_settlements_count", {
          count: fail,
        }),
      );
    setSelectedIds([]);
    refetch();
  };

  const unpaidRowIds = useMemo(
    () => rows.filter((r) => r.status !== "PAID").map((r) => r.id),
    [rows],
  );

  const handleMarkAllPaid = async () => {
    if (!unpaidRowIds.length) return;
    let ok = 0;
    let fail = 0;
    for (const id of unpaidRowIds) {
      try {
        await markPaid({ id }).unwrap();
        ok++;
      } catch {
        fail++;
      }
    }
    if (ok)
      toast.success(
        translate("company:payrolls.marked_settlements_as_paid_count", {
          count: ok,
        }),
      );
    if (fail)
      toast.error(
        translate("company:payrolls.failed_to_mark_settlements_count", {
          count: fail,
        }),
      );
    refetch();
  };

  const handleRowMarkPaid = async (id: number) => {
    try {
      await markPaid({ id }).unwrap();
      toast.success(
        translate("company:payrolls.final_settlement_marked_as_paid"),
      );
      refetch();
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          translate("company:payrolls.failed_to_mark_as_paid"),
      );
    }
  };

  const selectionDef: SelectionDef = {
    selectedIds,
    onSelectionChange: setSelectedIds,
    rowIdKey: "id",
  };

  const handleGenerateWps = async () => {
    if (!selectedRunIds.length) return;
    try {
      const result = await toast.promise(
        generateWps({ runIds: selectedRunIds }).unwrap(),
        {
          loading: translate("company:payrolls.generating_wps_files"),
          success: (res) => {
            const d = res.data;
            const filesText = translate(
              "company:payrolls.wps_files_generated_count",
              { count: d.filesGenerated },
            );
            const itemsText = translate(
              "company:payrolls.wps_items_included_count",
              { count: d.totalItems },
            );
            return `${filesText} · ${itemsText}`;
          },
          error: (err: any) =>
            err?.data?.message ??
            err?.message ??
            translate("company:payrolls.failed_to_generate_wps_files"),
        },
      );
      if (result?.data) {
        setSelectedIds([]);
        refetch();
        const warnings = result.data.warnings ?? [];
        for (const w of warnings) {
          const label =
            w.code === "ZERO_NET_PAY"
              ? translate("company:payrolls.excluded_zero_net_pay_count", {
                  count: w.count,
                })
              : translate(
                  "company:payrolls.excluded_missing_wps_bank_data_count",
                  { count: w.count },
                );
          toast(label, { icon: "⚠️", duration: 6000 });
        }
      }
    } catch {
      /* toast.promise surfaces */
    }
  };

  const columns: ColumnDef<IPayslipListItem>[] = [
    {
      key: "employee",
      label: translate("common:labels.employee"),
      className: "w-[24%]",
      render: (r) => (
        <div className="flex items-center gap-3">
          <Avatar className="size-9">
            {r.employee.profileImage && (
              <AvatarImage
                src={r.employee.profileImage}
                alt={r.employee.fullName}
              />
            )}
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
              {getInitials(r.employee.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {r.employee.fullName}
            </span>
            <span className="text-[11px] text-gray-400 truncate">
              {r.employee.employeeCode}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "branchDept",
      label: translate("company:payrolls.branch_dept"),
      className: "w-[18%]",
      render: (r) => (
        <div className="flex flex-col min-w-0">
          <span className="text-sm text-gray-900 truncate">
            {r.branch?.name ?? "—"}
          </span>
          <span className="text-[11px] text-gray-400 truncate">
            {r.department?.name ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "country",
      label: translate("company:payrolls.country"),
      className: "w-[14%]",
      render: (r) => {
        if (!r.nationality) return <span className="text-gray-300">—</span>;
        return (
          <div className="flex items-center gap-2">
            <ReactCountryFlag
              countryCode={r.nationality.code}
              svg
              style={{ width: "1.4em", height: "1em", borderRadius: "1px" }}
            />
            <span className="text-sm text-gray-700 truncate">
              {r.nationality.name}
            </span>
          </div>
        );
      },
    },
    {
      key: "net",
      label: translate("company:payrolls.net_short"),
      className: "w-[12%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">
          {r.currency?.code ?? "—"} {formatAmount(r.netPay)}
        </span>
      ),
    },
    {
      key: "period",
      label: translate("company:payrolls.pay_period"),
      className: "w-[12%]",
      render: (r) => (
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">{r.payPeriod}</span>
          <span className="text-[11px] text-gray-400">
            {safeDate(r.payDate)}
          </span>
        </div>
      ),
    },
    {
      key: "paymentMethod",
      label: translate("company:payrolls.payment_type"),
      className: "w-[8%]",
      render: (r) => (
        <span className="text-xs text-gray-600">
          {r.paymentMethod
            ? translate(
                PAYMENT_METHOD_LABEL_KEYS[r.paymentMethod] ?? r.paymentMethod,
              )
            : "—"}
        </span>
      ),
    },
    {
      key: "status",
      label: translate("company:payrolls.payment_status"),
      className: "w-[10%]",
      render: (r) => (
        <Badge variant={statusToVariant(r.status)}>
          {STATUS_LABEL_KEYS[r.status]
            ? translate(STATUS_LABEL_KEYS[r.status])
            : r.status}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: translate("company:payrolls.actions"),
      className: "w-[14%]",
      render: (r) => (
        <div className="flex items-center gap-2">
          <ViewAction
            tooltip={translate("company:payrolls.view")}
            onClick={() =>
              navigate(
                ROUTES.HOME.COMPANY.PAYROLL.FINAL_SETTLEMENT_DETAIL(r.id),
              )
            }
          />
          <Button
            type="button"
            variant="outline-success"
            size="icon-sm"
            disabled={isDownloading}
            aria-label={translate("company:payrolls.download_settlement")}
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={translate(
              "company:payrolls.download_settlement",
            )}
            onClick={() => download(r.id)}
          >
            <Download size={14} />
          </Button>
          {r.status !== "PAID" && (
            <PermissionButton
              type="button"
              section={PLATFORM_SECTION_ENUM.PAYROLL}
              action="edit"
              mode="disable"
              variant="ghost"
              size="sm"
              className="text-success gap-1"
              disabled={isMarkingPaid}
              onClick={() => handleRowMarkPaid(r.id)}
            >
              <CheckCircle size={14} />
              {translate("company:payrolls.mark_paid")}
            </PermissionButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageDataStats
        data={statCards}
        loading={statsLoading}
        className="sm:grid-cols-3 lg:grid-cols-5"
      />
      <div className="rounded-xs border border-border bg-white flex flex-col gap-4 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 w-80 bg-white">
            <Search size={14} className="text-gray-400" />
            <input
              type="text"
              value={tp.search}
              onChange={(e) => tp.setSearch(e.target.value)}
              placeholder={translate(
                "company:payrolls.search_employee_name_status",
              )}
              className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-hidden"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              value={branch}
              onChange={(v) => tp.setFilter("branch", v)}
              options={branchOptions}
              placeholder={translate("common:ui_kit.branch")}
            />
            <FilterSelect
              value={department}
              onChange={(v) => tp.setFilter("department", v)}
              options={departmentOptions}
              placeholder={translate("common:labels.department")}
            />
            <FilterSelect
              value={status}
              onChange={(v) => tp.setFilter("status", v)}
              options={buildStatusOptions(translate)}
              placeholder={translate("company:payrolls.status")}
            />
            <PermissionButton
              type="button"
              section={PLATFORM_SECTION_ENUM.PAYROLL}
              action="edit"
              mode="disable"
              variant="outline"
              size="sm"
              className="h-9 gap-1.5"
              disabled={isMarkingPaid || unpaidRowIds.length === 0}
              onClick={handleMarkAllPaid}
            >
              <CheckCircle size={13} />
              {translate("company:payrolls.mark_all_as_paid")}
            </PermissionButton>
            <PermissionButton
              type="button"
              section={PLATFORM_SECTION_ENUM.PAYROLL}
              action="edit"
              mode="disable"
              variant="primary"
              size="sm"
              className="h-9 gap-1.5"
              disabled={isMarkingPaid || selectedUnpaidItemIds.length === 0}
              onClick={handleMarkPaid}
            >
              <CheckCircle size={13} />
              {isMarkingPaid
                ? translate("common:states.marking")
                : selectedUnpaidItemIds.length > 0
                  ? translate("company:payrolls.mark_as_paid_count", {
                      count: selectedUnpaidItemIds.length,
                    })
                  : translate("company:payrolls.mark_as_paid")}
            </PermissionButton>
            <PermissionButton
              type="button"
              section={PLATFORM_SECTION_ENUM.PAYROLL}
              action="create"
              mode="disable"
              size="sm"
              className="h-9 gap-1"
              disabled={isGeneratingWps || selectedRunIds.length === 0}
              onClick={handleGenerateWps}
            >
              <FileCheck2 size={14} />
              {isGeneratingWps
                ? translate("common:states.generating")
                : selectedRunIds.length > 0
                  ? translate("company:payrolls.generate_wps_count", {
                      count: selectedRunIds.length,
                    })
                  : translate("company:payrolls.generate_wps")}
            </PermissionButton>
          </div>
        </div>

        <Table<IPayslipListItem>
          noPadding
          columns={columns}
          data={rows}
          loading={isFetching}
          selection={selectionDef}
          pagination={{
            totalPages,
            currentPage: tp.page,
            onPageChange: tp.setPage,
          }}
        />
      </div>
    </div>
  );
};

export default CompanyFinalSettlements;
